import emailjs from '@emailjs/browser';

export type EmailRecipient = 'shop' | 'customer' | 'both';

const SHOP_EMAIL = 'ametliktouks@gmail.com';
const EMAILJS_INIT_KEY = 'JveHxSEYo9cyKqJ74';
const EMAILJS_SERVICE = 'service_p298pqg';
const EMAILJS_TEMPLATE = 'template_vpeq4zx';
const PLACEHOLDER_IMG =
  'https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg';

export interface OrderEmailItem {
  name: string;
  price: number;
  quantity: number;
  image?: string;
}

export interface OrderEmailInfo {
  orderId: string;
  email: string;
  customerName: string;
  customerPhone?: string;
  shippingMethod?: string;
  shippingAddress?: string;
  paymentMethod?: string;
  orderTotal: number;
  items: OrderEmailItem[];
}

function buildProductsHtml(items: OrderEmailItem[]): string {
  return (
    items
      ?.map(
        (item) => `
      <tr>
        <td style="padding: 12px;">
          <div style="display: flex; align-items: center;">
            <img src="${item.image || PLACEHOLDER_IMG}"
                 alt="${item.name}"
                 class="product-image"
                 style="width: 50px; height: 50px; object-fit: cover; border-radius: 4px; margin-right: 12px;">
            <span>${item.name}</span>
          </div>
        </td>
        <td style="padding: 12px; text-align: center;">${item.quantity}</td>
        <td style="padding: 12px; text-align: right;">${item.price?.toFixed(2) || '0.00'}€</td>
        <td style="padding: 12px; text-align: right;">${((item.price || 0) * (item.quantity || 1)).toFixed(2)}€</td>
      </tr>
    `
      )
      .join('') || ''
  );
}

/**
 * Sends the order confirmation email to the chosen recipient(s).
 * Unlike the checkout success flow, this performs NO duplicate suppression —
 * it is meant to be triggered manually from the admin dashboard.
 * Returns the list of addresses the email was successfully sent to.
 */
export async function sendConfirmationEmail(
  orderInfo: OrderEmailInfo,
  recipient: EmailRecipient
): Promise<string[]> {
  emailjs.init(EMAILJS_INIT_KEY);

  const productsHtml = buildProductsHtml(orderInfo.items || []);

  let emailShippingMethod = orderInfo.shippingMethod || '';
  let emailShippingAddress = '';
  if (orderInfo.shippingMethod === 'Tulen ise järgi') {
    emailShippingMethod = 'Isiklik järeletulemine';
    emailShippingAddress = '';
  } else if (orderInfo.shippingAddress) {
    emailShippingAddress = orderInfo.shippingAddress;
  }

  const customerEmail = orderInfo.email?.toLowerCase()?.trim();

  // Build the target list based on the chosen recipient mode.
  const targets: { email: string; name: string; isCustomer: boolean }[] = [];
  if ((recipient === 'customer' || recipient === 'both') && customerEmail) {
    targets.push({ email: orderInfo.email, name: orderInfo.customerName, isCustomer: true });
  }
  if (recipient === 'shop' || recipient === 'both') {
    targets.push({ email: SHOP_EMAIL, name: 'Tõuks.ee', isCustomer: false });
  }

  if (targets.length === 0) {
    throw new Error('Kliendi e-posti aadress puudub.');
  }

  const sentTo: string[] = [];

  for (const target of targets) {
    const templateParams = {
      to_name: target.name,
      to_email: target.email,
      reply_to: target.isCustomer ? SHOP_EMAIL : orderInfo.email,
      order_id: orderInfo.orderId,
      customer_name: orderInfo.customerName,
      customer_email: orderInfo.email,
      customer_phone: orderInfo.customerPhone || '',
      shipping_method: emailShippingMethod,
      shipping_address: emailShippingAddress,
      payment_method: orderInfo.paymentMethod || '',
      products_html: productsHtml,
      subtotal: orderInfo.orderTotal?.toFixed(2) || '0.00',
      shipping: 'TASUTA',
      total: orderInfo.orderTotal?.toFixed(2) || '0.00',
      email: target.email,
    };

    await emailjs.send(
      EMAILJS_SERVICE,
      EMAILJS_TEMPLATE,
      templateParams,
      import.meta.env.VITE_EMAILJS_PUBLIC_KEY
    );
    sentTo.push(target.email);

    if (targets.length > 1) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  return sentTo;
}
